import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { BaseLayout } from '../../../interfaces/base-layout.interface';
import { ClientCvTemplate } from '../../../interfaces/client-cv-template.interface';
import { ClientCvTemplateService } from '../../templates/client-cv-template.service';
import { NotifierService } from '../../../shared/notifier.service';

@Component({
  selector: 'bst-cv-studio-pane-custom',
  templateUrl: './studio-pane-custom.component.html',
  styleUrls: ['./studio-pane-custom.component.scss'],
  standalone: false,
})
export class StudioPaneCustomComponent implements OnChanges, OnDestroy {
  @Input() template!: ClientCvTemplate;
  @Input() clientId!: number;
  @Input() baseLayouts: BaseLayout[] = [];
  @Output() templateUpdated = new EventEmitter<ClientCvTemplate>();
  @Output() templateDeleted = new EventEmitter<number>();
  @Output() replaceFiles = new EventEmitter<ClientCvTemplate>();

  public form: FormGroup;
  public savedAt: number | null = null;
  public fieldErrors: { [field: string]: string } = {};

  private readonly save$ = new Subject<void>();
  private readonly subs: Subscription[] = [];
  private destroyed = false;
  private lastTemplateId?: number;

  constructor(
    private readonly fb: FormBuilder,
    private readonly service: ClientCvTemplateService,
    private readonly translate: TranslateService,
    private readonly toastr: NbToastrService,
    private readonly notifier: NotifierService,
  ) {
    this.form = this.fb.group({
      name:           ['', [Validators.required, Validators.maxLength(80)]],
      base_layout_id: ['', Validators.required],
    });

    this.subs.push(this.save$.pipe(debounceTime(500)).subscribe(() => this.saveNow()));
    this.subs.push(this.form.valueChanges.subscribe(() => this.save$.next()));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['template']) {
      // Re-patch the form only when the template identity changes (a different
      // template was selected). On a same-id re-emit (the parent fetching the
      // fresh template after a save), keep the user's in-progress typing so
      // the autosave round-trip can't overwrite newer keystrokes.
      const nextId = this.template?.id;
      const isNewTemplate = this.lastTemplateId !== nextId;
      this.lastTemplateId = nextId;
      if (isNewTemplate) {
        this.form.patchValue({
          name:           this.template.name,
          base_layout_id: this.template.base_layout_id,
        }, { emitEvent: false });
        this.savedAt = null;
        this.fieldErrors = {};
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.subs.forEach(s => s.unsubscribe());
  }

  public baseLayoutName(id: string): string {
    return this.baseLayouts.find(b => b.id === id)?.name ?? id;
  }

  public thumbnailUrl(): string | null {
    // Inline preview shows the server-rendered sample (chrome + placeholder
    // fields), so the user sees exactly where CV content will land. Backend
    // caches by template.updated_at so renders only run when metadata changes.
    return this.service.sampleImageUrl(
      this.clientId,
      this.template.id,
      this.template.updated_at,
      this.template.base_layout_id,
    );
  }

  public previewLoading = false;

  public async openPreview(): Promise<void> {
    if (this.previewLoading) return;
    this.previewLoading = true;
    try {
      const url = this.service.samplePdfUrl(this.clientId, this.template.id);
      let resp: Response;
      try {
        resp = await fetch(url, { credentials: 'include' });
      } catch (e) {
        console.error('studio-pane-custom: preview fetch failed', e, { url });
        this.toastr.danger(String(e), 'Preview download failed');
        return;
      }
      if (!resp.ok) {
        console.error('studio-pane-custom: preview HTTP error', { url, status: resp.status });
        this.toastr.danger(`HTTP ${resp.status}`, 'Preview download failed');
        return;
      }
      const blob = await resp.blob();
      if (blob.size < 100) {
        console.error('studio-pane-custom: preview empty/short blob', { url, size: blob.size });
        this.toastr.danger('Empty response', 'Preview download failed');
        return;
      }
      const objectUrl = URL.createObjectURL(blob);
      const filename = `cv-template-${this.template.name.replace(/[^A-Za-z0-9._-]+/g, '-').toLowerCase()}-sample.pdf`;
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      this.toastr.success(filename, 'Downloaded');
    } finally {
      this.previewLoading = false;
    }
  }

  public requestReplace(): void {
    this.replaceFiles.emit(this.template);
  }

  public async confirmDelete(): Promise<void> {
    const ok = await this.notifier.confirm(
      'cv_templates.delete_confirm_title',
      this.translate.instant('cv_templates.delete_confirm_body', { name: this.template.name }),
      'cv_templates.delete_button',
      'danger',
    );
    if (!ok) return;
    this.service.delete(this.clientId, this.template.id).subscribe({
      next: () => {
        this.toastr.success('', this.translate.instant('cv_templates.deleted_toast'));
        this.templateDeleted.emit(this.template.id);
      },
      error: err => {
        console.error('studio-pane-custom: delete failed', err);
        this.toastr.danger(
          this.translate.instant('cv_templates.errors.delete_failed') || 'Could not delete the template.',
          this.translate.instant('error') || 'Error',
        );
      },
    });
  }

  private saveNow(): void {
    if (this.destroyed || this.form.invalid) return;
    const v = this.form.value;
    this.fieldErrors = {};
    this.service.update(this.clientId, this.template.id, {
      name:           v.name,
      base_layout_id: v.base_layout_id,
    }).subscribe({
      next: updated => {
        this.savedAt = Date.now();
        this.templateUpdated.emit(updated);
      },
      error: err => {
        // 422 = field-level validation; surface inline. Anything else is a
        // real save failure — without a toast the autosave UI looks like
        // it worked while the changes silently disappeared.
        const fe = err?.error?.errors ?? {};
        for (const key of Object.keys(fe)) this.fieldErrors[key] = (fe[key] || [])[0] ?? '';
        if (err?.status !== 422) {
          console.error('studio-pane-custom: save failed', err);
          this.toastr.danger(
            this.translate.instant('cv_templates.errors.save_failed') || 'Could not save changes.',
            this.translate.instant('error') || 'Error',
          );
        }
      },
    });
  }
}
